/**
 * Per-event-group extra `TriggerField[]` definitions for the Clerk
 * catalog. Split out from `event-metadata.ts` so that file stays
 * under the workspace's 1000-line per-module budget while the
 * per-event `CLERK_EVENT_METADATA` catalog itself stays in one place.
 *
 * Pure data: no runtime behaviour lives here. The catalog composes
 * these field-sets with `buildPayloadSchema` from
 * `event-metadata.ts`.
 */
import type { IntegrationTriggerField } from '@/lib/triggers/types'

type TriggerField = IntegrationTriggerField

export const USER_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'username',
    type: 'string',
    required: false,
    description: 'Clerk username when set on the user.',
    exampleValue: 'janedoe',
  },
  {
    key: 'imageUrl',
    type: 'string',
    required: false,
    description: 'Clerk profile image URL when set on the user.',
    exampleValue: 'https://img.clerk.com/xxxxxx',
  },
  {
    key: 'lastSignInAt',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) of the user’s last sign-in when known.',
    exampleValue: 1_716_883_200,
  },
]

export const ORG_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'name',
    type: 'string',
    required: false,
    description: 'Organization display name.',
    exampleValue: 'Acme Corp',
  },
  {
    key: 'slug',
    type: 'string',
    required: false,
    description: 'Organization slug.',
    exampleValue: 'acme-corp',
  },
  {
    key: 'membersCount',
    type: 'int',
    required: false,
    description: 'Current number of members in the organization.',
    exampleValue: 1,
  },
  {
    key: 'maxAllowedMemberships',
    type: 'int',
    required: false,
    description: 'Plan-imposed seat cap for the organization, when set.',
    exampleValue: 5,
  },
]

export const ORG_INVITATION_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'roleName',
    type: 'string',
    required: false,
    description: 'Human-readable role name.',
    exampleValue: 'Member',
  },
  {
    key: 'status',
    type: 'string',
    required: false,
    description:
      'Invitation status — `pending` on `.created`, `accepted` on `.accepted`, `revoked` on `.revoked`.',
    exampleValue: 'pending',
  },
  {
    key: 'expiresAt',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) the invitation expires at, when set.',
    exampleValue: 1_717_487_999,
  },
]

export const MEMBERSHIP_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'organizationName',
    type: 'string',
    required: false,
    description: 'Organization display name.',
    exampleValue: 'Acme Corp',
  },
  {
    key: 'organizationSlug',
    type: 'string',
    required: false,
    description: 'Organization slug.',
    exampleValue: 'acme-corp',
  },
  {
    key: 'roleName',
    type: 'string',
    required: false,
    description: 'Human-readable role name.',
    exampleValue: 'Member',
  },
]

/**
 * Billing-payer identity fields were removed from the slim user-facing
 * schema (`userId` / `organizationId` were IDs). Per-event extras now
 * stand on their own — the payer context still lands on the synced
 * contact via the contact mapper, which reads from the raw extracted
 * payload, not the schema-filtered one.
 */
export const PAYMENT_ATTEMPT_FIELDS: Array<TriggerField> = [
  {
    key: 'status',
    type: 'string',
    required: false,
    description: 'Payment attempt status — one of `pending`, `paid`, `failed`.',
    exampleValue: 'pending',
  },
  {
    key: 'chargeType',
    type: 'string',
    required: false,
    description:
      'Charge classification — `recurring` for subscription cycles, `one_time` for ad-hoc charges.',
    exampleValue: 'recurring',
  },
  {
    key: 'grandTotalAmount',
    type: 'int',
    required: false,
    description:
      'Total amount due in the smallest currency unit (cents for USD).',
    exampleValue: 2200,
  },
  {
    key: 'grandTotalFormatted',
    type: 'string',
    required: false,
    description: 'Pre-formatted total ready for templating (e.g. `$22.00`).',
    exampleValue: '$22.00',
  },
  {
    key: 'currency',
    type: 'string',
    required: false,
    description: 'ISO 4217 currency code.',
    exampleValue: 'USD',
  },
  {
    key: 'failedReason',
    type: 'string',
    required: false,
    description: 'Failure reason string when `status === "failed"`.',
    exampleValue: 'card_declined',
  },
  {
    key: 'billingDate',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) the payment was scheduled to be billed.',
    exampleValue: 1_716_883_200,
  },
]

export const SUBSCRIPTION_FIELDS: Array<TriggerField> = [
  {
    key: 'status',
    type: 'string',
    required: false,
    description: 'Subscription status (e.g. `active`, `past_due`, `canceled`).',
    exampleValue: 'active',
  },
  {
    key: 'activeAt',
    type: 'int',
    required: false,
    description: 'Unix timestamp (seconds) the subscription became active.',
    exampleValue: 1_716_883_200,
  },
  {
    key: 'planName',
    type: 'string',
    required: false,
    description:
      'Display name of the primary plan (from `event.data.items[0].plan.name`).',
    exampleValue: 'Pro Plan',
  },
  {
    key: 'planSlug',
    type: 'string',
    required: false,
    description: 'Plan slug suitable for routing decisions.',
    exampleValue: 'pro-plan',
  },
  {
    key: 'planAmount',
    type: 'int',
    required: false,
    description: 'Plan amount in the smallest currency unit (cents for USD).',
    exampleValue: 2000,
  },
  {
    key: 'currency',
    type: 'string',
    required: false,
    description: 'ISO 4217 currency code for the plan amount.',
    exampleValue: 'USD',
  },
  {
    key: 'periodStart',
    type: 'int',
    required: false,
    description: 'Unix timestamp (seconds) the current billing period started.',
    exampleValue: 1_716_883_200,
  },
  {
    key: 'periodEnd',
    type: 'int',
    required: false,
    description: 'Unix timestamp (seconds) the current billing period ends.',
    exampleValue: 1_719_561_600,
  },
]

export const SUBSCRIPTION_ITEM_FIELDS: Array<TriggerField> = [
  {
    key: 'planName',
    type: 'string',
    required: false,
    description: 'Display name of the plan attached to this item.',
    exampleValue: 'Pro Plan',
  },
  {
    key: 'planSlug',
    type: 'string',
    required: false,
    description: 'Plan slug suitable for routing decisions.',
    exampleValue: 'pro-plan',
  },
  {
    key: 'planAmount',
    type: 'int',
    required: false,
    description: 'Plan amount in the smallest currency unit.',
    exampleValue: 2000,
  },
  {
    key: 'currency',
    type: 'string',
    required: false,
    description: 'ISO 4217 currency code.',
    exampleValue: 'USD',
  },
  {
    key: 'status',
    type: 'string',
    required: false,
    description:
      'Subscription item status — typically reflects the event (e.g. `active`, `past_due`, `canceled`, `abandoned`).',
    exampleValue: 'active',
  },
  {
    key: 'isFreeTrial',
    type: 'boolean',
    required: false,
    description: 'True when the item is still inside its free-trial window.',
    exampleValue: true,
  },
  {
    key: 'periodStart',
    type: 'int',
    required: false,
    description: 'Unix timestamp (seconds) the current item period started.',
    exampleValue: 1_716_883_200,
  },
  {
    key: 'periodEnd',
    type: 'int',
    required: false,
    description: 'Unix timestamp (seconds) the current item period ends.',
    exampleValue: 1_719_561_600,
  },
  {
    key: 'updatedAt',
    type: 'int',
    required: false,
    description: 'Unix timestamp (seconds) the item was last updated.',
    exampleValue: 1_716_969_600,
  },
]

export const WAITLIST_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'status',
    type: 'string',
    required: false,
    description: 'Waitlist entry status — typically `pending` on create.',
    exampleValue: 'pending',
  },
]
